import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';

import {
  ActionIcon,
  Badge,
  Container,
  Group,
  Modal,
  Pagination,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconInfoCircle } from '@tabler/icons-react';

import { useAuthContext } from '../../hooks/api/AuthProvider';
import { Duck } from '../../hooks/api/types';
import { useDucks } from '../../hooks/api/useDucks';
import { LoadError, LoadingSpinner, NoDucksFound, Tag } from '../shared';
import { PillInput } from '../pill-input';
import { TableHeader } from './header';

export const DuckTable = () => {
  const { token, isAuthenticated, logout } = useAuthContext();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [selectedDuck, setSelectedDuck] = useState<Duck | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [sortBy, setSortBy] = useState<'name' | 'last_seen' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const isSmallScreen = useMediaQuery('(max-width: 768px)') ?? false;

  // Convert sort state to ordering parameter for API
  const ordering = sortBy ? (sortDirection === 'desc' ? `-${sortBy}` : sortBy) : undefined;

  const { ducks, loading, error, fetchDucks, totalCount, addTag, removeTag } = useDucks(
    {
      token: token || undefined,
      onAuthError: logout,
    },
    {
      autoFetch: false,
      page: currentPage,
      pageSize: pageSize,
      ordering: ordering,
    },
  );

  const hasFetchedRef = useRef(false);
  const initialPageRef = useRef(currentPage);
  const prevOrderingRef = useRef(ordering);

  useEffect(() => {
    if (isAuthenticated && token && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchDucks();
    }
  }, [isAuthenticated, token]); // fetchDucks intentionally omitted to prevent loop

  useEffect(() => {
    // Fetch on page change or ordering change
    if (hasFetchedRef.current && (currentPage !== initialPageRef.current || ordering !== prevOrderingRef.current)) {
      fetchDucks();
      prevOrderingRef.current = ordering;
    }
  }, [currentPage, ordering]); // fetchDucks intentionally omitted to prevent loop

  const handleSort = (field: 'name' | 'last_seen') => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
    initialPageRef.current = 1;
  };

  const formatRelativeDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    
    const date = DateTime.fromISO(dateString);
    if (!date.isValid) return '-';
    
    return date.toRelative();
  };

  const formatFullDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    
    const date = DateTime.fromISO(dateString);
    if (!date.isValid) return '-';
    
    return date.toLocaleString(DateTime.DATETIME_FULL);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <LoadError failedItem="ducks" error={error} />;
  }

  if (ducks.length === 0) {
    return <NoDucksFound />;
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Stack gap="md">
      <Table striped highlightOnHover>
        <TableHeader 
          isSmallScreen={isSmallScreen} 
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
        <Table.Tbody>
          {ducks.map((duck) => (
            <Table.Tr key={duck.id}>
              <Table.Td>
                <Text fw={500}>{duck.name}</Text>
                {duck.pool && (
                  <Badge variant="dot" color="orange" size="sm" radius="xs">
                    {duck.pool.replace(/_/g, ' ')}
                  </Badge>
                )}
              </Table.Td>
              {!isSmallScreen && (
                <Table.Td>
                  <Text ff="monospace" size="sm">
                    {duck.mac_address || '-'}
                  </Text>
                </Table.Td>
              )}
              <Table.Td>
                <Text ff="monospace" size="sm">
                  {duck.ip_address || '-'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Tooltip label={formatFullDate(duck.last_seen)} position="top">
                  <Text size="sm">
                    {formatRelativeDate(duck.last_seen)}
                  </Text>
                </Tooltip>
              </Table.Td>
              {!isSmallScreen && (
                <Table.Td>
                  <PillInput
                    tags={duck.tags}
                    onAddTag={async (tagName) => {
                      try {
                        await addTag(duck.id, tagName);
                        await fetchDucks(); // Refresh the table
                      } catch (error) {
                        console.error('Failed to add tag:', error);
                      }
                    }}
                    onRemoveTag={async (tagName) => {
                      try {
                        await removeTag(duck.id, tagName);
                        await fetchDucks(); // Refresh the table
                      } catch (error) {
                        console.error('Failed to remove tag:', error);
                      }
                    }}
                    placeholder="Add tag..."
                  />
                </Table.Td>
              )}
              {!isSmallScreen && (
                <Table.Td>
                  {duck.additional_info ? (
                    <Text size="sm" c="dimmed">
                      {typeof duck.additional_info === 'object'
                        ? Object.keys(duck.additional_info).length + ' fields'
                        : 'Has data'}
                    </Text>
                  ) : (
                    <Text c="dimmed" size="sm">
                      -
                    </Text>
                  )}
                </Table.Td>
              )}
              {isSmallScreen && (
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={() => {
                      setSelectedDuck(duck);
                      open();
                    }}
                  >
                    <IconInfoCircle size={16} data-testid="info-icon" />
                  </ActionIcon>
                </Table.Td>
              )}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Container fluid p={0}>
        <Group justify="center">
          <Pagination
            value={currentPage}
            onChange={setCurrentPage}
            total={totalPages}
            size="sm"
          />
        </Group>
      </Container>

      <Modal opened={opened} onClose={close} title="Device Details" size="md">
        {selectedDuck && (
          <Stack gap="md">
            <div>
              <Text fw={500} size="sm" c="dimmed">
                MAC Address
              </Text>
              <Text ff="monospace">{selectedDuck.mac_address || '-'}</Text>
            </div>
            {isSmallScreen && (
              <div>
                <Text fw={500} size="sm" c="dimmed">
                  Tags
                </Text>
                <PillInput
                  tags={selectedDuck.tags}
                  onAddTag={(tagName) => addTag(selectedDuck.id, tagName)}
                  onRemoveTag={(tagName) => removeTag(selectedDuck.id, tagName)}
                  placeholder="Add tag..."
                />
              </div>
            )}
            <div>
              <Text fw={500} size="sm" c="dimmed">
                Additional Info
              </Text>
              {selectedDuck.additional_info ? (
                <Text size="sm">
                  {typeof selectedDuck.additional_info === 'object'
                    ? JSON.stringify(selectedDuck.additional_info, null, 2)
                    : selectedDuck.additional_info}
                </Text>
              ) : (
                <Text c="dimmed" size="sm">
                  -
                </Text>
              )}
            </div>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};